import type { FeatDefinition } from '@character-creator/types';
import { ignusFeats } from '../rulesets/ignus';

/**
 * SRD 5.1 Feats
 * The SRD only includes the Grappler feat.
 * Additional feats can be added for homebrew content.
 */
const srdFeats: FeatDefinition[] = [
  {
    id: 'grappler',
    name: 'Grappler',
    description:
      "You've developed the skills necessary to hold your own in close-quarters grappling. You gain the following benefits:",
    prerequisites: {
      abilityScores: {
        strength: 13,
      },
    },
    benefits: [
      'You have advantage on attack rolls against a creature you are grappling.',
      'You can use your action to try to pin a creature grappled by you. To do so, make another grapple check. If you succeed, you and the creature are both restrained until the grapple ends.',
    ],
    source: 'srd-5.1',
  },
];

/**
 * Combined feats from all sources
 */
export const feats: FeatDefinition[] = [...srdFeats, ...ignusFeats];

// Lookup map for efficient access
export const featMap = new Map<string, FeatDefinition>(feats.map((f) => [f.id, f]));

// Helper function
export function getFeat(id: string): FeatDefinition | undefined {
  return featMap.get(id);
}

/**
 * Get all feats, optionally filtered by source
 */
export function getFeats(source?: string): FeatDefinition[] {
  return source ? feats.filter((f) => f.source === source) : feats;
}

/**
 * Check if a character meets the prerequisites for a feat
 */
export function meetsFeatPrerequisites(
  feat: FeatDefinition,
  characterData: {
    abilityScores: Record<string, number>;
    level?: number;
    raceId?: string;
    classId?: string;
    proficiencies?: string[];
    hasSpellcasting?: boolean;
  }
): { meets: boolean; reason?: string } {
  const prereqs = feat.prerequisites;
  if (!prereqs) {
    return { meets: true };
  }

  // Check ability score requirements
  if (prereqs.abilityScores) {
    for (const [ability, minScore] of Object.entries(prereqs.abilityScores)) {
      const currentScore = characterData.abilityScores[ability] || 0;
      if (currentScore < minScore) {
        return {
          meets: false,
          reason: `Requires ${ability.charAt(0).toUpperCase() + ability.slice(1)} ${minScore} (you have ${currentScore})`,
        };
      }
    }
  }

  // Check level requirement
  if (prereqs.level && (characterData.level || 1) < prereqs.level) {
    return {
      meets: false,
      reason: `Requires level ${prereqs.level}`,
    };
  }

  // Check race requirement
  if (prereqs.race && characterData.raceId !== prereqs.race) {
    return {
      meets: false,
      reason: `Requires ${prereqs.race} race`,
    };
  }

  // Check class requirement
  if (prereqs.class && characterData.classId !== prereqs.class) {
    return {
      meets: false,
      reason: `Requires ${prereqs.class} class`,
    };
  }

  // Check proficiency requirement
  if (prereqs.proficiency) {
    const hasProficiency = characterData.proficiencies?.includes(prereqs.proficiency);
    if (!hasProficiency) {
      return {
        meets: false,
        reason: `Requires ${prereqs.proficiency} proficiency`,
      };
    }
  }

  // Check spellcasting requirement
  if (prereqs.spellcasting && !characterData.hasSpellcasting) {
    return {
      meets: false,
      reason: 'Requires spellcasting ability',
    };
  }

  return { meets: true };
}

/**
 * Get levels at which ASI/Feat choices are available for a class
 * Most classes get ASI at 4, 8, 12, 16, 19
 * Fighter gets extra at 6 and 14
 * Rogue gets extra at 10
 */
export function getASILevels(classId: string): number[] {
  const baseASILevels = [4, 8, 12, 16, 19];

  switch (classId) {
    case 'fighter':
      return [4, 6, 8, 12, 14, 16, 19];
    case 'rogue':
      return [4, 8, 10, 12, 16, 19];
    default:
      return baseASILevels;
  }
}
